import { useState, useEffect } from "react";
import { Crown, Sparkles, Infinity as InfinityIcon, Image as ImageIcon, X, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { t } from "@/lib/i18n";
import { restorePremiumByEmail } from "@/lib/api";

export default function PaywallModal({
  open,
  onClose,
  onUnlock,
  onRestored,
  loading,
  freeLimit = 5,
}) {
  const [mode, setMode] = useState("pay"); // "pay" | "restore"
  const [email, setEmail] = useState("");
  const [restoring, setRestoring] = useState(false);

  // Reset to the default Pay view whenever the modal is (re)opened so the
  // user doesn't accidentally land back in the restore form after dismissing.
  useEffect(() => {
    if (open) {
      setMode("pay");
      setEmail("");
      setRestoring(false);
    }
  }, [open]);

  if (!open) return null;

  const benefits = [
    { Icon: InfinityIcon, key: "paywallBenefitUnlimited" },
    { Icon: Sparkles, key: "paywallBenefitFilters" },
    { Icon: ImageIcon, key: "paywallBenefitAspect" },
    { Icon: Crown, key: "paywallBenefitFuture" },
  ];

  const handleRestore = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error(t("restoreError"));
      return;
    }
    setRestoring(true);
    try {
      const res = await restorePremiumByEmail(email.trim());
      if (res?.is_premium) {
        toast.success(t("restoreSuccess"));
        onRestored?.();
      } else {
        toast.error(t("restoreNotFound"));
      }
    } catch (err) {
      console.error("restore failed:", err);
      toast.error(t("restoreError"));
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div
      data-testid="paywall-modal"
      className="fixed inset-0 z-[95] flex items-end sm:items-center justify-center"
    >
      <div
        onClick={onClose}
        className="absolute inset-0 bg-black/75 backdrop-blur-md"
      />
      <div className="relative w-full sm:max-w-md mx-0 sm:mx-4 bg-gradient-to-b from-zinc-900 to-black border-t sm:border border-amber-400/20 rounded-t-3xl sm:rounded-3xl shadow-[0_20px_80px_rgba(251,191,36,0.18)] slide-up overflow-hidden">
        {/* close on desktop */}
        <button
          data-testid="paywall-close-button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 h-9 w-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center active:scale-95 z-10"
        >
          <X className="h-4 w-4 text-white/70" strokeWidth={1.8} />
        </button>

        <div className="flex justify-center pt-3 pb-1 sm:hidden">
          <div className="h-1 w-10 rounded-full bg-white/15" />
        </div>

        {/* Glow */}
        <div className="relative px-7 pt-8 pb-2">
          <div className="absolute inset-x-0 top-0 h-32 bg-amber-400/10 blur-3xl pointer-events-none" />
          <div className="relative flex flex-col items-center gap-3">
            <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-amber-300 to-amber-500 flex items-center justify-center shadow-[0_8px_24px_rgba(251,191,36,0.35)]">
              <Crown className="h-7 w-7 text-black" strokeWidth={2} />
            </div>
            <h2 className="font-display text-3xl font-light text-white tracking-tight text-center">
              {mode === "restore" ? t("restoreTitle") : t("paywallTitle")}
            </h2>
            <p className="text-sm text-white/65 text-center max-w-[300px]">
              {mode === "restore"
                ? t("restoreDescription")
                : t("paywallSubtitle", freeLimit)}
            </p>
          </div>
        </div>

        {mode === "restore" ? (
          <form
            onSubmit={handleRestore}
            className="px-7 pt-5 pb-8 flex flex-col gap-3"
          >
            <input
              data-testid="restore-email-input"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("restoreEmailPlaceholder")}
              className="h-12 w-full rounded-xl bg-white/5 border border-white/15 px-4 text-white placeholder-white/30 focus:outline-none focus:border-amber-400/40 transition-colors"
            />
            <button
              type="submit"
              data-testid="restore-submit-button"
              disabled={restoring}
              className="h-14 w-full rounded-full bg-gradient-to-r from-amber-300 to-amber-500 text-black font-display font-semibold text-base active:scale-[0.98] transition-transform disabled:opacity-60 shadow-[0_8px_24px_rgba(251,191,36,0.3)]"
            >
              {restoring ? "…" : t("restoreSubmit")}
            </button>
            <button
              type="button"
              data-testid="restore-back-button"
              onClick={() => {
                setEmail("");
                setMode("pay");
              }}
              className="h-10 text-sm text-white/55 active:text-white/80 transition-colors flex items-center justify-center gap-1.5"
            >
              <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.8} />
              {t("paywallSkip")}
            </button>
          </form>
        ) : (
          <>
            <div className="px-7 pt-5 pb-3 flex flex-col gap-3">
              {benefits.map(({ Icon, key }) => (
                <div key={key} className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-full bg-amber-400/10 border border-amber-400/25 flex items-center justify-center shrink-0">
                    <Icon className="h-4 w-4 text-amber-300" strokeWidth={1.8} />
                  </div>
                  <span className="text-sm text-white/85">{t(key)}</span>
                </div>
              ))}
            </div>

            <div className="px-7 pt-3 pb-8 flex flex-col gap-3">
              <button
                data-testid="paywall-unlock-button"
                onClick={onUnlock}
                disabled={loading}
                className="h-14 w-full rounded-full bg-gradient-to-r from-amber-300 to-amber-500 text-black font-display font-semibold text-base active:scale-[0.98] transition-transform disabled:opacity-60 disabled:cursor-not-allowed shadow-[0_8px_24px_rgba(251,191,36,0.3)]"
              >
                {loading ? t("paymentPending") : t("paywallCta")}
              </button>
              <button
                data-testid="paywall-restore-button"
                onClick={() => setMode("restore")}
                disabled={loading}
                className="h-10 text-sm text-amber-300/90 active:text-amber-200 transition-colors underline-offset-2 hover:underline"
              >
                {t("restorePremium")}
              </button>
              <button
                data-testid="paywall-skip-button"
                onClick={onClose}
                disabled={loading}
                className="h-10 text-sm text-white/55 active:text-white/80 transition-colors"
              >
                {t("paywallSkip")}
              </button>
              <p className="text-[10px] text-white/35 text-center mt-1 leading-relaxed">
                {t("paywallSecureNote")}
                <br />
                <a
                  href="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline text-white/55 hover:text-white/85"
                >
                  Obchodné podmienky
                </a>
                {" · "}
                <a
                  href="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline text-white/55 hover:text-white/85"
                >
                  Ochrana údajov
                </a>
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
